import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Form,
  FormField,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import type { ProcessedTool } from "@arcadeai/ui-kit/arcade/arcade";
import { ToolkitPickerTrigger } from "@arcadeai/ui-kit/features/toolkit-picker/components/toolkit-picker-trigger";
import { ToolkitPicker } from "@arcadeai/ui-kit/toolkit-picker";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Loader2 } from "lucide-react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { WarningMissingRequirements } from "@/app/tools/components/warning-missing-requirements";
import { useSearchEmptyTracking } from "@/hooks/use-search-empty-tracking";
import { useToolkitPickerData } from "@/hooks/use-toolkit-picker-data";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { removeEmptyValues } from "@/lib/utils";
import { useCreateMcpGateway } from "../../api/hooks";
import { sanitizeGatewayPayload } from "../../lib/sanitize-gateway-payload";
import {
  type McpServer,
  type McpServerCreateRequest,
  mcpServerCreateSchema,
} from "../../types";
import { AllowedPassthroughHeadersField } from "../form/allowed-passthrough-headers-field";
import { AuthTypeFormField } from "../form/auth-type-form-field";
import { ConsentSkipClientIdsField } from "../form/consent-skip-client-ids-field";
import { DescriptionFormField } from "../form/description-form-field";
import { InstructionsFormField } from "../form/instructions-form-field";
import { NameFormField } from "../form/name-form-field";
import { SlugFormField } from "../form/slug-form-field";
import { UrlPreview } from "../form/url-preview";

const SHOW_MAX_TOOLKITS = 6;

const GATEWAY_DEFAULTS: McpServerCreateRequest = {
  name: "my-arcade-gateway",
  description: "",
  instructions: "",
  slug: "",
  status: "active",
  auth_type: "arcade",
  user_source_id: null,
  tool_filter: { allowed_tools: [] },
  allowed_passthrough_headers: [],
  consent_skip_client_ids: [],
};

const DEFAULT_TOOLS_HELPER_TEXT = "Choose which tools this gateway may call.";

const PREFILLED_TOOLS_HELPER_TEXT =
  "Pre-filled from your current playground tool selection. Adjust which tools this gateway may call.";

type GatewayCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSelectedToolkits: string[];
  initialSelectedTools: string[];
  /** Navigate to the gateway list with connect after create. Defaults to true. */
  navigateOnCreate?: boolean;
  onSuccess?: (gateway: McpServer) => void | Promise<void>;
};

/**
 * Outer shell — renders the Dialog chrome immediately so the overlay
 * appears without delay, then loads the form body inside a Suspense
 * boundary (useToolkitPickerData uses useSuspenseQuery).
 *
 * Base UI Dialog unmounts content when closed, so the body gets a fresh
 * instance (and fresh state from props) each time the dialog opens.
 */
export function GatewayCreateDialog({
  open,
  onOpenChange,
  initialSelectedToolkits,
  initialSelectedTools,
  navigateOnCreate,
  onSuccess,
}: GatewayCreateDialogProps) {
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[min(90vh,720px)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create MCP gateway</DialogTitle>
          <DialogDescription>
            Create a new gateway and choose which tools it can use. Fine-tune
            settings under Advanced.
          </DialogDescription>
        </DialogHeader>
        <Suspense fallback={<GatewayCreateDialogSkeleton />}>
          <GatewayCreateDialogBody
            initialSelectedToolkits={initialSelectedToolkits}
            initialSelectedTools={initialSelectedTools}
            navigateOnCreate={navigateOnCreate}
            onOpenChange={onOpenChange}
            onSuccess={onSuccess}
          />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function GatewayCreateDialogSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-3.5 w-48" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-3.5 w-64" />
      </div>
      <Separator />
      <Skeleton className="h-[52px] w-full rounded-lg" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

type GatewayCreateDialogBodyProps = Omit<GatewayCreateDialogProps, "open">;

function GatewayCreateDialogBody({
  onOpenChange,
  onSuccess,
  navigateOnCreate = true,
  initialSelectedToolkits,
  initialSelectedTools,
}: GatewayCreateDialogBodyProps) {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const navigate = useNavigate();
  const handleSearchEmpty = useSearchEmptyTracking();
  const { data: toolkitCollection, isFetching: toolkitCollectionFetching } =
    useToolkitPickerData();

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selection, setSelection] = useState({
    toolkits: initialSelectedToolkits,
    tools: initialSelectedTools,
  });

  const initialToolNames = useMemo(
    () => initialSelectedTools.map((fqn) => fqn.split("@")[0]),
    [initialSelectedTools]
  );

  const toolsHelperText =
    initialSelectedTools.length > 0
      ? PREFILLED_TOOLS_HELPER_TEXT
      : DEFAULT_TOOLS_HELPER_TEXT;

  const form = useForm<McpServerCreateRequest>({
    resolver: zodResolver(mcpServerCreateSchema),
    defaultValues: {
      ...GATEWAY_DEFAULTS,
      tool_filter: { allowed_tools: initialToolNames },
    },
  });

  const { mutate: createGateway, isPending } = useCreateMcpGateway();

  const handlePickerChange = useCallback(
    (sel: { selectedToolkits: string[]; selectedTools: string[] }) => {
      setSelection({
        toolkits: sel.selectedToolkits,
        tools: sel.selectedTools,
      });
      const names = sel.selectedTools.map((fqn) => fqn.split("@")[0]);
      form.setValue("tool_filter.allowed_tools", names, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );

  const selectedToolObjects = useMemo((): ProcessedTool[] => {
    if (!toolkitCollection || selection.tools.length === 0) {
      return [];
    }
    return selection.tools
      .map((fqn) =>
        toolkitCollection.tools.find((t) => t.fully_qualified_name === fqn)
      )
      .filter((tool): tool is ProcessedTool => Boolean(tool));
  }, [toolkitCollection, selection.tools]);

  const handleSubmit = useCallback(
    (values: McpServerCreateRequest) => {
      const cleanedValues = removeEmptyValues(values);
      const sanitized = sanitizeGatewayPayload(cleanedValues);
      createGateway(sanitized, {
        onSuccess: async (data) => {
          onOpenChange(false);
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          await onSuccess?.(data as McpServer);
          if (navigateOnCreate) {
            navigate({
              to: "/orgs/$organizationId/projects/$projectId/mcp-gateways",
              params: { organizationId, projectId },
              // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
              search: { connect: (data as McpServer).id },
            });
          }
        },
      });
    },
    [
      createGateway,
      onOpenChange,
      onSuccess,
      navigateOnCreate,
      navigate,
      organizationId,
      projectId,
    ]
  );

  const handleInvalid = useCallback(() => {
    toast.error("Some required information is missing or incorrect");
  }, []);

  const pickerKey = `${toolkitCollection?.tools.length ?? 0}`;

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
      >
        <NameFormField autoFocus control={form.control} />

        <div className="space-y-2">
          <FormLabel>Toolkits &amp; tools</FormLabel>
          <div className="flex items-center gap-2">
            <ToolkitPicker
              initialProps={{
                selectedToolkits: selection.toolkits,
                selectedTools: selection.tools,
                committedToolkits: selection.toolkits,
                committedTools: selection.tools,
              }}
              key={pickerKey}
              onSearchEmpty={handleSearchEmpty}
              onSelectionChange={handlePickerChange}
              selectionMode="multi"
              toolkitCollection={toolkitCollection}
            >
              <ToolkitPickerTrigger
                className="w-full"
                countType="tools"
                loading={!toolkitCollection || toolkitCollectionFetching}
                maxVisibleIcons={SHOW_MAX_TOOLKITS}
              />
            </ToolkitPicker>
          </div>
          <p className="text-muted-foreground text-sm">{toolsHelperText}</p>
          <FormField
            control={form.control}
            name="tool_filter.allowed_tools"
            render={() => <FormMessage />}
          />
          {selectedToolObjects.length > 0 ? (
            <WarningMissingRequirements
              className="mt-2"
              tools={selectedToolObjects}
            />
          ) : null}
        </div>

        <Separator />

        <Collapsible onOpenChange={setAdvancedOpen} open={advancedOpen}>
          <CollapsibleTrigger
            render={
              <button
                className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-border bg-transparent p-3 text-left transition-colors hover:bg-muted/50"
                type="button"
              />
            }
          >
            <div>
              <span className="font-medium text-sm">Advanced</span>
              <p className="text-muted-foreground text-xs">
                Auth type, slug, description, instructions, headers, consent
              </p>
            </div>
            <ChevronDown
              className={`size-4 shrink-0 text-muted-foreground transition-transform ${advancedOpen ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent keepMounted>
            <div className="mt-4 space-y-4 border-border border-t pt-4">
              <UrlPreview slug={form.watch("slug")} />
              <DescriptionFormField control={form.control} />
              <InstructionsFormField control={form.control} />
              <SlugFormField control={form.control} />

              <AuthTypeFormField control={form.control} />

              <AllowedPassthroughHeadersField control={form.control} />

              <ConsentSkipClientIdsField control={form.control} />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => <input type="hidden" {...field} />}
        />

        <Button
          className="w-full"
          disabled={isPending || toolkitCollectionFetching}
          type="submit"
        >
          <span className="flex items-center justify-center gap-3">
            {!!isPending && <Loader2 className="size-4 animate-spin" />}
            Create MCP gateway
          </span>
        </Button>
      </form>
    </Form>
  );
}
